// @ts-nocheck
/**
 * KL HTTP 客户端实现
 *
 * 本文件实现了 KL HTTP 客户端的核心功能，包括：
 * - 默认 HTTP 请求执行器
 * - HTTP 客户端类
 * - 拦截器支持
 * - 超时处理
 * - 响应数据解析
 *
 */

import type {
	KLHttpBaseOptions,
	KLHttpError,
	KLHttpExecutor,
	KLHttpInterceptor,
	KLHttpRequestOptions,
	KLHttpResponse,
} from './types'

/**
 * 默认 HTTP 请求执行器
 *
 * 基于浏览器原生 fetch API 实现的 HTTP 请求执行器
 * 支持自动解析响应数据、处理请求参数、构建完整 URL 等功能
 *
 * @class KLHttpExecutorDefault
 * @implements KLHttpExecutor
 */
export class KLHttpExecutorDefault implements KLHttpExecutor {
	/** 执行器配置选项 */
	options: KLHttpBaseOptions

	/**
	 * 构造函数
	 *
	 * @param options HTTP 基础配置选项
	 */
	public constructor(options: KLHttpBaseOptions = {}) {
		this.options = {
			baseURL: '',
			timeout: 10000,
			headers: {},
			...options,
		}
	}

	/**
	 * 发送 HTTP 请求
	 *
	 * @param options 请求配置选项
	 * @returns Promise<KLHttpResponse<T>> 响应对象
	 * @template T 响应数据类型
	 */
	async fetch<T = any>(options: KLHttpRequestOptions): Promise<KLHttpResponse<T>> {
		const url = this._buildUrl(options)
		const requestInit: RequestInit = {
			method: options.method || 'GET',
			headers: options.headers,
			credentials: options.withCredentials ? 'include' : 'same-origin',
		}

		// 为 POST、PUT、PATCH 请求添加请求体
		if (options.data && ['POST', 'PUT', 'PATCH'].includes(options.method || 'GET')) {
			// 如果data是对象，序列化为JSON字符串
			if (typeof options.data === 'object' && options.data !== null) {
				requestInit.body = JSON.stringify(options.data)
				// 设置Content-Type为application/json
				if (!requestInit.headers) {
					requestInit.headers = {}
				}
				requestInit.headers['Content-Type'] = 'application/json'
			} else {
				requestInit.body = options.data
			}
		}

		const responseRaw = await fetch(url, requestInit)
		const responseData = await this._parseResponse<T>(responseRaw)

		return {
			data: responseData,
			status: responseRaw.status,
			statusText: responseRaw.statusText,
			headers: this._parseHeaders(responseRaw.headers),
			options: options,
		}
	}

	/**
	 * 解析响应数据
	 *
	 * 根据 Content-Type 自动选择解析方式：
	 * - application/json: 解析为 JSON
	 * - text/*: 解析为文本
	 * - 其他: 解析为 Blob
	 *
	 * @param response 原始响应对象
	 * @returns Promise<T> 解析后的数据
	 * @template T 数据类型
	 * @private
	 */
	private async _parseResponse<T>(response: Response): Promise<T> {
		const contentType = response.headers.get('content-type')

		if (contentType && contentType.includes('application/json')) {
			return response.json()
		}

		if (contentType && contentType.includes('text/')) {
			return (await response.text()) as T
		}

		return (await response.blob()) as T
	}

	/**
	 * 解析响应头
	 *
	 * 将 Headers 对象转换为普通的键值对对象
	 *
	 * @param headers Headers 对象
	 * @returns Record<string, string> 响应头对象
	 * @private
	 */
	private _parseHeaders(headers: Headers): Record<string, string> {
		const result: Record<string, string> = {}
		headers.forEach((value, key) => {
			result[key] = value
		})
		return result
	}

	/**
	 * 构建完整 URL
	 *
	 * 处理基础 URL、相对 URL 和查询参数，构建完整的请求 URL
	 *
	 * @param options 请求配置选项
	 * @returns string 完整的 URL
	 * @private
	 */
	private _buildUrl(options: KLHttpRequestOptions): string {
		const url = options.url || ''
		const baseURL = options.baseURL || ''
		const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`
		const params = options.params

		if (!params) return fullUrl

		const urlObj = new URL(fullUrl)
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				urlObj.searchParams.append(key, String(value))
			}
		})

		return urlObj.toString()
	}
}

/**
 * HTTP 客户端响应处理函数类型
 *
 * 用于在请求完成后对响应数据进行自定义处理
 *
 * @template TResp 处理后的响应数据类型
 * @template TRespRaw 原始响应数据类型
 */
export type KLHttpClientOnResponse<TResp = any, TRespRaw = any> = (
	response: KLHttpResponse<TRespRaw>,
) => Promise<KLHttpResponse<TResp>> | KLHttpResponse<TResp>

/**
 * KL HTTP 客户端类
 *
 * 提供完整的 HTTP 客户端功能，包括：
 * - 请求拦截器支持
 * - 响应拦截器支持
 * - 错误拦截器支持
 * - 超时处理
 * - 便捷的 HTTP 方法封装
 *
 * @class KLHttpClient
 */
class KLHttpClient {
	/** HTTP 请求执行器 */
	protected executor: KLHttpExecutor = new KLHttpExecutorDefault()

	/** 拦截器映射表 */
	protected interceptors: Map<number, KLHttpInterceptor> = new Map()

	/** 下一个拦截器 ID */
	protected nextInterceptorId = 1

	/**
	 * 发送 HTTP 请求
	 *
	 * 核心请求方法，支持拦截器、超时处理和错误处理
	 *
	 * @param options 请求配置选项
	 * @param onResponse 响应处理函数
	 * @returns Promise<KLHttpResponse<TResp>> 处理后的响应对象
	 * @template TResp 响应数据类型
	 * @template TRespRaw 原始响应数据类型
	 */
	async request<TResp = any, TRespRaw = any>(
		options: KLHttpRequestOptions,
		onResponse?: KLHttpClientOnResponse<TResp, TRespRaw>,
	): Promise<KLHttpResponse<TResp>> {
		let processedOptions = this._mergeOptions(options)

		try {
			// 执行请求拦截器
			for (const interceptor of this.interceptors.values()) {
				if (!interceptor.onRequest) continue
				processedOptions = await interceptor.onRequest(processedOptions)
			}

			// 处理超时
			const timeoutPromise = new Promise<never>((_, reject) => {
				setTimeout(() => {
					reject(new Error(`Request timeout after ${processedOptions.timeout}ms`))
				}, processedOptions.timeout)
			})

			const response = await Promise.race([
				this.executor.fetch<TRespRaw>(processedOptions),
				timeoutPromise,
			])

			// 设置默认响应处理函数
			if (!onResponse) {
				onResponse = (response: KLHttpResponse<TRespRaw>) => {
					return response
				}
			}

			// 执行响应处理函数
			let processedResponse = await onResponse(response)

			// 执行响应拦截器
			for (const interceptor of this.interceptors.values()) {
				if (!interceptor.onResponse) continue
				processedResponse = await interceptor.onResponse<TResp>(processedResponse)
			}

			return processedResponse
		} catch (error) {
			// 构建 HTTP 错误对象
			const httpError: KLHttpError = {
				message: error instanceof Error ? error.message : 'Unknown error',
				options: processedOptions,
				status: error instanceof Response ? error.status : undefined,
				statusText: error instanceof Response ? error.statusText : undefined,
			}

			// 执行错误拦截器
			for (const interceptor of this.interceptors.values()) {
				if (!interceptor.onError) continue
				await interceptor.onError(httpError)
			}

			throw httpError
		}
	}

	/**
	 * 发送 GET 请求
	 *
	 * @param url 请求 URL
	 * @param options 请求配置选项（排除 method）
	 * @returns Promise<KLHttpResponse<T>> 响应对象
	 * @template T 响应数据类型
	 */
	async get<T = any>(
		url: string,
		options?: Omit<KLHttpRequestOptions, 'method'>,
	): Promise<KLHttpResponse<T>> {
		return this.request<T>({ ...options, method: 'GET', url })
	}

	/**
	 * 发送 POST 请求
	 *
	 * @param url 请求 URL
	 * @param data 请求体数据
	 * @param options 请求配置选项（排除 method 和 data）
	 * @returns Promise<KLHttpResponse<T>> 响应对象
	 * @template T 响应数据类型
	 */
	async post<T = any>(
		url: string,
		data?: any,
		options?: Omit<KLHttpRequestOptions, 'method' | 'data'>,
	): Promise<KLHttpResponse<T>> {
		return this.request<T>({ ...options, method: 'POST', url, data })
	}

	/**
	 * 发送 PUT 请求
	 *
	 * @param url 请求 URL
	 * @param data 请求体数据
	 * @param options 请求配置选项（排除 method 和 data）
	 * @returns Promise<KLHttpResponse<T>> 响应对象
	 * @template T 响应数据类型
	 */
	async put<T = any>(
		url: string,
		data?: any,
		options?: Omit<KLHttpRequestOptions, 'method' | 'data'>,
	): Promise<KLHttpResponse<T>> {
		return this.request<T>({ ...options, method: 'PUT', url, data })
	}

	/**
	 * 设置 HTTP 执行器
	 *
	 * @param executor HTTP 执行器实例
	 */
	setExecutor(executor: KLHttpExecutor) {
		this.executor = executor
	}

	/**
	 * 添加拦截器
	 *
	 * @param interceptor 拦截器对象
	 * @returns number 拦截器 ID，用于后续删除
	 */
	addInterceptor(interceptor: KLHttpInterceptor): number {
		const id = this.nextInterceptorId++
		this.interceptors.set(id, interceptor)
		return id
	}

	/**
	 * 移除拦截器
	 *
	 * @param id 拦截器 ID
	 */
	removeInterceptor(id: number): void {
		this.interceptors.delete(id)
	}

	/**
	 * 清空所有拦截器
	 */
	clearInterceptors(): void {
		this.interceptors.clear()
	}

	/**
	 * 合并配置选项
	 *
	 * 将执行器的基础配置与请求配置合并，处理请求头合并
	 *
	 * @param options 请求配置选项
	 * @returns KLHttpRequestOptions 合并后的配置选项
	 * @private
	 */
	private _mergeOptions(options?: KLHttpRequestOptions): KLHttpRequestOptions {
		return {
			...this.executor.options,
			...options,
			headers: {
				...this.executor.options.headers,
				...options?.headers,
			},
		}
	}
}

/**
 * 全局 HTTP 客户端实例
 *
 * 提供单例模式的 HTTP 客户端，可以在整个应用中共享使用
 * 支持拦截器、超时处理、错误处理等功能
 *
 * @example
 * ```typescript
 * // 添加请求拦截器
 * klHttpClient.addInterceptor({
 *   onRequest: (options) => {
 *     options.headers = { ...options.headers, 'Authorization': 'Bearer token' }
 *     return options
 *   }
 * })
 *
 * // 发送请求
 * const response = await klHttpClient.get<UserInfo>('/api/user/123')
 * ```
 */
export const klHttpClient: KLHttpClient = new KLHttpClient()
